import React, { useState } from 'react';
import {
  alpha,
  Box,
  Button,
  Divider,
  IconButton,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ArchiveOutlined,
  Check,
  Close,
  EditOutlined,
  UnarchiveOutlined,
} from '@mui/icons-material';

import focusRing from '../../theme/focus';
import { ChatSessionListItem } from './types';
import formatSessionTime from './formatSessionTime';

const PLACEHOLDER_ROW_COUNT = 5;

const PinguSessionsSidebar = ({
  sessions,
  archivedMode,
  fullWidth,
  hasMore,
  loading,
  loadingMore,
  pageCount,
  currentSessionId,
  sessionProcessingStatuses,
  onArchiveSession,
  onFetchMore,
  onHide,
  onResetPagination,
  onSelectSession,
  onToggleArchivedMode,
  onRenameSession,
}: {
  sessions: ChatSessionListItem[];
  archivedMode: boolean;
  fullWidth: boolean;
  hasMore: boolean;
  loading: boolean;
  loadingMore: boolean;
  pageCount: number;
  currentSessionId: string | null;
  sessionProcessingStatuses: Map<string, boolean>;
  onArchiveSession: (sessionId: string, isArchived: boolean) => Promise<boolean>;
  onFetchMore: () => void;
  onHide: () => void;
  onResetPagination: () => void;
  onSelectSession: (sessionId: string) => void;
  onToggleArchivedMode: () => void;
  onRenameSession: (sessionId: string, name: string) => Promise<boolean>;
}) => {
  const [editingSessionId, setEditingSessionId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const [archivingSessionId, setArchivingSessionId] = useState<string | null>(null);
  const [savingSessionId, setSavingSessionId] = useState<string | null>(null);

  const startEditing = (chatSession: ChatSessionListItem) => {
    setEditingSessionId(chatSession.id);
    setEditingName(chatSession.name);
  };

  const cancelEditing = () => {
    setEditingSessionId(null);
    setEditingName('');
  };

  const saveEditing = async () => {
    if (!editingSessionId || !editingName.trim()) return;
    setSavingSessionId(editingSessionId);
    const saved = await onRenameSession(editingSessionId, editingName);
    setSavingSessionId(null);
    if (saved) cancelEditing();
  };

  const handleArchive = async (sessionId: string, isArchived: boolean) => {
    setArchivingSessionId(sessionId);
    await onArchiveSession(sessionId, isArchived);
    setArchivingSessionId(null);
  };

  const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
    if (!archivedMode || loading || loadingMore || !hasMore) return;

    const element = event.currentTarget;
    const distanceFromBottom = element.scrollHeight - element.scrollTop - element.clientHeight;
    if (distanceFromBottom < 32) onFetchMore();
  };

  const handleFetchMore = () => {
    if (!loadingMore) onFetchMore();
  };

  return (
    <Box
      sx={{
        width: fullWidth ? '100%' : 300,
        flexShrink: 0,
        borderRight: (theme) => (fullWidth ? 0 : `1px solid ${theme.palette.divider}`),
        backgroundColor: 'background.paper',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
      }}
    >
      <Box sx={{
        alignItems: 'center',
        display: 'flex',
        gap: 1,
        justifyContent: 'space-between',
        px: 3,
        py: 2,
      }}
      >
        <Typography variant="mediumTitle" noWrap sx={{ minWidth: 0 }}>
          {archivedMode ? 'Archived conversations' : 'Previous conversations'}
        </Typography>
        <Box sx={{ display: 'flex', flexShrink: 0, gap: 0.5 }}>
          <Tooltip title="Hide history">
            <IconButton size="small" onClick={onHide}>
              <Close fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={archivedMode ? 'Show active chats' : 'Show archived chats'}>
            <IconButton size="small" onClick={onToggleArchivedMode}>
              {archivedMode
                ? <UnarchiveOutlined fontSize="small" />
                : <ArchiveOutlined fontSize="small" />}
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      <Divider />
      <Stack
        spacing={0.5}
        sx={{
          p: 2,
          overflowY: 'auto',
          scrollbarWidth: 'thin',
          scrollbarColor: (theme) => `${alpha(theme.palette.emphasisText.main, 0.5)} transparent`,
          '&::-webkit-scrollbar': { width: 6 },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: (theme) => alpha(theme.palette.emphasisText.main, 0.5),
            borderRadius: 3,
          },
        }}
        onScroll={handleScroll}
      >
        {loading && Array.from({ length: PLACEHOLDER_ROW_COUNT }, (_, row) => row).map((row) => (
          <Box key={row} sx={{ px: 3, py: 2 }}>
            <Skeleton variant="text" animation={false} width={`${70 - (row % 3) * 12}%`} />
          </Box>
        ))}
        {!loading && sessions.length === 0 && (
          <Typography color="text.secondary" sx={{ px: 3, py: 1 }}>
            {archivedMode ? 'No archived conversations' : 'No previous conversations'}
          </Typography>
        )}
        {!loading && sessions.map((chatSession) => {
          const isCurrent = chatSession.id === currentSessionId;
          const isProcessing = sessionProcessingStatuses.has(chatSession.id)
            ? sessionProcessingStatuses.get(chatSession.id)
            : chatSession.processing;
          const isEditing = chatSession.id === editingSessionId;
          const isArchiving = chatSession.id === archivingSessionId;
          const isSaving = chatSession.id === savingSessionId;

          return (
            <Box
              key={chatSession.id}
              sx={{
                backgroundColor: isCurrent
                  ? (theme) => alpha(theme.palette.emphasisText.main, 0.08)
                  : 'transparent',
                borderRadius: 1.5,
                px: 3,
                py: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                '&:hover': {
                  backgroundColor: isCurrent
                    ? (theme) => alpha(theme.palette.emphasisText.main, 0.08)
                    : 'background.default',
                },
                '&:hover .pinguSessionActions, &:focus-within .pinguSessionActions': { display: 'flex' },
                '&:hover .pinguSessionTime, &:focus-within .pinguSessionTime': { display: 'none' },
                '@media (hover: none)': { '& .pinguSessionActions': { display: 'flex' } },
              }}
            >
              {isEditing ? (
                <>
                  <TextField
                    value={editingName}
                    autoFocus
                    size="small"
                    variant="standard"
                    disabled={isSaving}
                    onChange={(event) => setEditingName(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') saveEditing();
                      if (event.key === 'Escape') cancelEditing();
                    }}
                    inputProps={{ maxLength: 80 }}
                    sx={{ flex: 1, minWidth: 0 }}
                  />
                  <Tooltip title="Save">
                    <span>
                      <IconButton
                        size="small"
                        disabled={isSaving || !editingName.trim()}
                        onClick={() => saveEditing()}
                      >
                        <Check fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Cancel">
                    <span>
                      <IconButton size="small" disabled={isSaving} onClick={cancelEditing}>
                        <Close fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </>
              ) : (
                <>
                  <Box
                    component="button"
                    type="button"
                    onClick={() => onSelectSession(chatSession.id)}
                    aria-current={isCurrent ? 'true' : undefined}
                    sx={{
                      border: 0,
                      backgroundColor: 'transparent',
                      color: 'inherit',
                      textAlign: 'left',
                      cursor: 'pointer',
                      p: 0,
                      minWidth: 0,
                      flex: 1,
                      overflow: 'hidden',
                      borderRadius: 0.5,
                      '&:focus-visible': { boxShadow: focusRing },
                    }}
                  >
                    <Typography
                      noWrap
                      sx={{ fontWeight: isCurrent ? 'fontWeightMedium' : 'fontWeightRegular' }}
                    >
                      {chatSession.name}
                    </Typography>
                    {isProcessing && (
                      <Typography variant="body1" color="text.secondary" component="span" noWrap>
                        Working
                      </Typography>
                    )}
                  </Box>
                  {/* Held to the line height so the taller actions don't grow the row. */}
                  <Box sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    flexShrink: 0,
                    minWidth: 64,
                    height: 20,
                  }}
                  >
                    <Typography
                      component="time"
                      dateTime={chatSession.createdAt}
                      variant="body1"
                      color="text.secondary"
                      className="pinguSessionTime"
                      sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}
                    >
                      {formatSessionTime(chatSession.createdAt)}
                    </Typography>
                    <Box
                      className="pinguSessionActions"
                      sx={{
                        display: 'none',
                        flexShrink: 0,
                        gap: 0.25,
                      }}
                    >
                      <Tooltip title="Rename">
                        <IconButton size="small" onClick={() => startEditing(chatSession)}>
                          <EditOutlined fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={archivedMode ? 'Unarchive' : 'Archive'}>
                        <span>
                          <IconButton
                            size="small"
                            aria-label={archivedMode ? 'Unarchive' : 'Archive'}
                            disabled={isArchiving}
                            onClick={() => { handleArchive(chatSession.id, !archivedMode); }}
                          >
                            {archivedMode
                              ? <UnarchiveOutlined fontSize="small" />
                              : <ArchiveOutlined fontSize="small" />}
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  </Box>
                </>
              )}
            </Box>
          );
        })}
      </Stack>
      {archivedMode && (loadingMore || hasMore || pageCount > 1) && (
        <>
          <Divider />
          <Stack spacing={0.5} sx={{ p: 2 }}>
            {!loading && loadingMore && (
              <Typography color="text.secondary" sx={{ px: 3, py: 1 }}>
                Loading more...
              </Typography>
            )}
            {!loading && hasMore && (
              <Button
                size="small"
                variant="text"
                disabled={loadingMore}
                onClick={handleFetchMore}
                sx={{ alignSelf: 'stretch' }}
              >
                {archivedMode ? 'Load more archived chats' : 'Load more chats'}
              </Button>
            )}
            {!loading && pageCount > 1 && (
              <Button
                size="small"
                variant="text"
                disabled={loadingMore}
                onClick={onResetPagination}
                sx={{ alignSelf: 'stretch' }}
              >
                {archivedMode ? 'Show fewer archived chats' : 'Show fewer chats'}
              </Button>
            )}
          </Stack>
        </>
      )}
    </Box>
  );
};

export default PinguSessionsSidebar;
